import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  fetchUserThreads,
  fetchUserComments,
  fetchUserProfileInfo,
} from '../services/api';
import { useInstitution } from '../components/InstitutionContext';
import ThreadCard from '../components/ThreadCard';
import CommentCard from '../components/CommentCard';
import UserProfileSidebar from '../components/UserProfileSidebar';
import ThreadSearchBar from '../components/ThreadSearchBar';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RightSidebarContainer from '../components/RightSidebarContainer';
import CollapsibleSidebarSection from '../components/CollapsibleSidebarSection';
import { Avatar } from '@mui/material';
import { useAuth } from '../components/AuthContext';
import VisibilityFilterSidebar from '../components/VisibilityFilterSidebar';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilledButton from '../components/FilledButton';
import LoginPromptAlert from '../components/LoginPromptAlert';
import VerifyEmailPromptAlert from '../components/VerifyEmailPromptAlert';
import AddPostDialog from '../components/AddPostDialog';


const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

export default function UserActivityPage() {
  const { displayName } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const previousPath = location.state?.from || '/community';
  const { user, emailVerified } = useAuth();
  const verifiedUser = user && emailVerified;
  const unverifiedUser = user && !emailVerified;
  const { selectedInstitution } = useInstitution();
  const [threads, setThreads] = useState([]);
  const [comments, setComments] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [visibleCount, setVisibleCount] = useState(10);
  const [showLocked, setShowLocked] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const userDisplayName =
    user?.userprofile?.display_name || user?.display_name || user?.username;
  const isMyPage = verifiedUser && decodeURIComponent(displayName) === userDisplayName;

  useEffect(() => {
    const loadData = async () => {
      if (!selectedInstitution) {
        setThreads([]);
        setComments([]);
        setUserInfo(null);
        return;
      }
      setLoadingData(true);
      try {
        const [thData, cData, info] = await Promise.all([
          fetchUserThreads(displayName, selectedInstitution),
          fetchUserComments(displayName, selectedInstitution),
          fetchUserProfileInfo(displayName, selectedInstitution),
        ]);
        const mappedThreads = thData.map((thread) => ({
          ...thread,
          subtopicId: thread.subtopic_id || '',
          subtopicName: thread.subtopicName || thread.subtopic || '',
        }));
        const mappedComments = cData.map((comment) => ({
          ...comment,
          upvotes: comment.upvotes,
          downvotes: comment.downvotes,
          upvoted: comment.upvoted,
          downvoted: comment.downvoted,
          thread: {
            ...comment.thread,
            subtopicId: comment.thread?.subtopic_id || '',
            subtopicName:
              comment.thread?.subtopicName ||
              comment.thread?.subtopic ||
              '',
          },
        }));
        setThreads(mappedThreads);
        setComments(mappedComments);
        setUserInfo(info);
      } catch (err) {
        console.error('Failed to fetch user activity:', err);
      } finally {
        setLoadingData(false);
      }
    };
    loadData();
  }, [displayName, selectedInstitution]);

  const fetchMore = () => {
    setTimeout(() => {
      setVisibleCount((prev) => prev + 10);
    }, 500);
  };

  let list = [];
  if (activeTab === 'posts') {
    list = threads;
  } else if (activeTab === 'comments') {
    list = comments;
  } else {
    list = [...threads, ...comments];
  }

  if (searchQuery.trim() !== '') {
    const q = searchQuery.toLowerCase();
    list = list.filter((item) => {
      if (item.title) {
        return (
          item.title.toLowerCase().includes(q) ||
          stripHtml(item.content).toLowerCase().includes(q)
        );
      }
      const threadTitle = item.thread?.title || '';
      return (
        threadTitle.toLowerCase().includes(q) ||
        stripHtml(item.content).toLowerCase().includes(q)
      );
    });
  }

  list = list.filter((item) => {
    if (showLocked && showRemoved) {
      return item.is_locked || item.is_removed;
    }
    if (showLocked) {
      return item.is_locked;
    }
    if (showRemoved) {
      return item.is_removed;
    }
    return !item.is_locked && !item.is_removed;
  });

  if (sortBy === 'newest') {
    list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  } else if (sortBy === 'oldest') {
    list.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  } else if (sortBy === 'most_viewed') {
    list.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
  } else if (sortBy === 'most_commented') {
    list.sort((a, b) => (b.repliesCount || 0) - (a.repliesCount || 0));
  }

  const hasMore = visibleCount < list.length;

  return (
    <Box sx={{ maxWidth: '1200px', width: '100%', mx: 'auto', py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1, px: 4 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon color="success" />}
            onClick={() => navigate(previousPath)}
            sx={{ mb: 2 }}
          >
            Back
          </Button>

          <Typography variant="h4" gutterBottom>
            Activity by {decodeURIComponent(displayName)}
          </Typography>

          {isSmallScreen && (
            <>
              <CollapsibleSidebarSection
                title={userInfo?.display_name || userInfo?.username || ''}
                icon={
                  <Avatar
                    src={userInfo?.avatar || undefined}
                    alt={userInfo?.display_name || userInfo?.username}
                    sx={{ width: 24, height: 24 }}
                  />
                }
              >
                <UserProfileSidebar user={userInfo} />
              </CollapsibleSidebarSection>
              {isMyPage && (
                <CollapsibleSidebarSection
                  title="Filters"
                  icon={<FilterListIcon fontSize="small" color="success" />}
                >
                  <VisibilityFilterSidebar
                    showLocked={showLocked}
                    showRemoved={showRemoved}
                    setShowLocked={setShowLocked}
                    setShowRemoved={setShowRemoved}
                  />
                </CollapsibleSidebarSection>
              )}
              {!isMyPage && verifiedUser && (
                <Button
                  component={RouterLink}
                  to={`/community/user/${encodeURIComponent(
                    userDisplayName
                  )}`}
                  variant="outlined"
                  size="large"
                  sx={{ mb: 1 }}
                >
                  View My Posts
                </Button>
              )}
              {verifiedUser ? (
                <FilledButton
                  onClick={() => setPostOpen(true)}
                  text="Add a Post"
                  color="success"
                  size="large"
                  sx={{ mb: 2 }}
                  data-tour-id="community-add-post"
                />
              ) : !user ? (
                <LoginPromptAlert
                  from={location.pathname}
                  message="to Add a Post."
                  sx={{ mb: 2 }}
                />
              ) : (
                <VerifyEmailPromptAlert
                  message="to Add a Post."
                  sx={{ mb: 2 }}
                />
              )}
            </>
          )}

          {isSmallScreen ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  width: '100%',
                  mb: 2,
                }}
              >
                <ThreadSearchBar
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{ flexGrow: 1 }}
                />
                <FormControl size="small">
                  <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <MenuItem value="newest">Newest</MenuItem>
                    <MenuItem value="oldest">Oldest</MenuItem>
                    <MenuItem value="most_viewed">Most Viewed</MenuItem>
                    <MenuItem value="most_commented">Most Commented</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Tabs
                  value={activeTab}
                  onChange={(e, val) => setActiveTab(val)}
                  variant="fullWidth"
                  TabIndicatorProps={{ sx: { backgroundColor: 'success.main' } }}
                >
                  <Tab
                    label="All"
                    value="all"
                    sx={{ fontSize: '1rem', textTransform: 'none', minHeight: 40, py: 0, mb: 0 }}
                  />
                  <Tab
                    label="Posts"
                    value="posts"
                    sx={{ fontSize: '1rem', textTransform: 'none', minHeight: 40, py: 0, mb: 0 }}
                  />
                  <Tab
                    label="Comments"
                    value="comments"
                    sx={{ fontSize: '1rem', textTransform: 'none', minHeight: 40, py: 0, mb: 0 }}
                  />
                </Tabs>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                mb: 2,
                gap: 2,
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(e, val) => setActiveTab(val)}
                TabIndicatorProps={{ sx: { backgroundColor: 'success.main' } }}
              >
                <Tab
                  label="All"
                  value="all"
                  sx={{ fontSize: '1rem', textTransform: 'none', minHeight: 40, py: 0, mb: 0 }}
                />
                <Tab
                  label="Posts"
                  value="posts"
                  sx={{ fontSize: '1rem', textTransform: 'none', minHeight: 40, py: 0, mb: 0 }}
                />
                <Tab
                  label="Comments"
                  value="comments"
                  sx={{ fontSize: '1rem', textTransform: 'none', minHeight: 40, py: 0, mb: 0 }}
                />
              </Tabs>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 'auto' }}>
                <ThreadSearchBar
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <FormControl size="small">
                  <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <MenuItem value="newest">Newest</MenuItem>
                    <MenuItem value="oldest">Oldest</MenuItem>
                    <MenuItem value="most_viewed">Most Viewed</MenuItem>
                    <MenuItem value="most_commented">Most Commented</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          )}

          <InfiniteScroll
            dataLength={Math.min(visibleCount, list.length)}
            next={fetchMore}
            hasMore={hasMore}
            loader={<Typography variant="body2" sx={{ my: 2 }}>Loading...</Typography>}
          >
            {list.slice(0, visibleCount).map((item) => {
              const isPost = item.title !== undefined;
              if (activeTab === 'posts') {
                return (
                  <ThreadCard
                    key={`th-${item.id}`}
                    thread={item}
                    disableVoting={loadingData}
                  />
                );
              }
              if (activeTab === 'comments') {
                return (
                  <CommentCard
                    key={`co-${item.id}`}
                    comment={{
                      ...item,
                      upvotes: item.upvotes,
                      downvotes: item.downvotes,
                      upvoted: item.upvoted,
                      downvoted: item.downvoted,
                    }}
                    disableVoting={loadingData}
                  />
                );
              }
              return isPost ? (
                <ThreadCard key={`th-${item.id}`} thread={item} disableVoting={loadingData} />
              ) : (
                <CommentCard
                  key={`co-${item.id}`}
                  comment={{
                    ...item,
                    upvotes: item.upvotes,
                    downvotes: item.downvotes,
                    upvoted: item.upvoted,
                    downvoted: item.downvoted,
                  }}
                  disableVoting={loadingData}
                />
              );
            })}
          </InfiniteScroll>
        </Box>

        {!isSmallScreen && (
          <RightSidebarContainer sticky topOffset={64} sx={{ ml: 3, mt: 6 }}>
            {!isMyPage && verifiedUser && (
              <Button
                component={RouterLink}
                to={`/community/user/${encodeURIComponent(
                  userDisplayName
                )}`}
                variant="outlined"
                size="large"
                sx={{ width: '100%', mb: 1 }}
              >
                View My Posts
              </Button>
            )}
            {verifiedUser ? (
              <FilledButton
                onClick={() => setPostOpen(true)}
                text="Add a Post"
                color="success"
                size="large"
                sx={{ width: '100%', mb: 2 }}
                data-tour-id="community-add-post"
              />
            ) : !user ? (
              <LoginPromptAlert
                from={location.pathname}
                message="to Add a Post."
                sx={{ width: '100%', mb: 2 }}
              />
            ) : (
              <VerifyEmailPromptAlert
                message="to Add a Post."
                sx={{ width: '100%', mb: 2 }}
              />
            )}
            <UserProfileSidebar user={userInfo} />
            {isMyPage && (
              <VisibilityFilterSidebar
                showLocked={showLocked}
                showRemoved={showRemoved}
                setShowLocked={setShowLocked}
                setShowRemoved={setShowRemoved}
              />
            )}
          </RightSidebarContainer>
        )}
      </Box>
      <AddPostDialog
        open={postOpen}
        onClose={() => setPostOpen(false)}
        previousPath={location.pathname}
      />
    </Box>
  );
}
